/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {GeoFenceStatus} from '@ion/core/src/types/entities';
import Accordion from '@ion/dls/src/components/accordion/ExpansionPanel';
import Slider from '@ion/dls/src/components/slider/Slider';
import Switch from '@ion/dls/src/components/switch/Switch';
import {isGeoFenceEnabled} from './LayerChooser';
import {
  LayerSettingsHeader,
  LayerSettingsMarks as Marks,
  LayerSettingsProps,
  LayerSettingsStyles,
  LayerSettingsSubLayersProps
} from './LayerSettings';
import styles from './LayerChooser.module.scss';

// defines the geofence layer prop definitions ...
export interface GeoFenceLayerSettingsProps extends LayerSettingsProps {
  onOpacityChange: Function,
  onToggleExpand?: Function,
  onBorderOnlyChange?: Function,
  onGeofenceLabelChange?: Function,
  opacity: number,
  expandedLayer?: string | null,
  borderOnly?: boolean;
  geofenceLabelState?: boolean,
  selectedLayers?: string[],
  geofenceStatus?: GeoFenceStatus,
  layers?: string[];
};

// defines the unique identifier for the Geofence Layer
export const GEOFENCE_LAYER = 'geofence';

// defines the styles used by the GeofenceLayerSettings
const useStyles = makeStyles(theme => (LayerSettingsStyles()));

/**
 * @OpacitySlider
 *
 * Defines the Opacity slider to handle the opacity of the Geofence Graphics
 */
const OpacitySlider = ({opacity = 1, changeOpacity}: LayerSettingsSubLayersProps) => {
  const classes           = useStyles();
  const [value, setValue] = useState(opacity);

  useEffect(() => {
    setValue(opacity);
  }, [opacity]);

  useEffect(() => {
    changeOpacity(value);
  }, [value]);

  const handleChange = (e, value) => {
    setValue(value);
  };

  return (
    <div className={classes.geofenceContainer} style={{marginTop: '-20px'}}>
      <div className={styles.subLayersSlider}>
        <span className={styles.subLayersHeader}>{translate('maps:geofence.opacity')}</span>
        <Slider step={0.1}
          marks={Marks}
          value={value}
          max={1}
          onSlide={function(e, val) { handleChange(e, val); }} />
      </div>
    </div>
  );
};

/**
 * @GeofenceLayerSettings
 *
 * The Geofence Layer Settings provides a component which allows users to select
 * specific Geofence layer related attributes to be displayed within a DLSMap.
 *
 * The GeofenceLayerSettings component represents the 'Geofence Layers' DLS feature:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/01_DLS_Components?node-id=1904%3A13962
 *
 * ```typescript
 *
 *   <GeofenceLayerSettings isEnabled={}
 *     onToggle={}
 *     onOpacityChange={}
 *     opacity={}
 *     map={} />
 *
 * ```
 */
export const GeofenceLayerSettings = ({
  layers,
  opacity,
  onToggle,
  isEnabled,
  borderOnly,
  expandedLayer,
  onToggleExpand,
  selectedLayers,
  geofenceStatus,
  onOpacityChange,
  geofenceLabelState,
  onBorderOnlyChange,
  onGeofenceLabelChange
}: GeoFenceLayerSettingsProps) => {
  const classes: any                = useStyles();
  const label                       = translate('maps:geofence.header');
  const [defaultLayer, setSelected] = useState(isEnabled);

  useEffect(() => {
    setSelected(isEnabled);
  }, [isEnabled]);

  const onToggleLayer = useCallback(({target:{checked}}) => {
    setSelected(checked);
    onToggle(GEOFENCE_LAYER, checked, label);
  }, [layers]);

  const changeOpacity = useCallback(opacity => {
    onOpacityChange(opacity);
  }, []);

  const onBorderChange = useCallback(({target:{checked}}: ChangeEvent<HTMLInputElement>) => {
    onBorderOnlyChange(checked);
  }, []);

  const onLabelChange = useCallback(({target:{checked}}: ChangeEvent<HTMLInputElement>) => {
    onGeofenceLabelChange(checked);
  }, []);

  useEffect(() => {
    setSelected(isGeoFenceEnabled(selectedLayers));
  }, [selectedLayers]);

  useEffect(() => {
    if (geofenceStatus) {
      const isCreate = geofenceStatus?.isCreate;
      const success  = geofenceStatus?.success;
      if (isCreate && success) {
        setSelected(true);
        onToggle(GEOFENCE_LAYER, true, label);
      }
    }
  }, [geofenceStatus, label, GEOFENCE_LAYER]);

  const Details = (
    <>
      <OpacitySlider changeOpacity={changeOpacity} opacity={opacity} />
      <Switch label={translate('maps:geofence.borderOnly')}
        checked={borderOnly}
        marginLeft='35px'
        onChange={onBorderChange}/>
      <Switch label={translate('maps:geofence.geofenceLabel')}
        checked={geofenceLabelState}
        marginLeft='35px'
        onChange={onLabelChange}/>
    </>
  );

  return (
    <div className={classes.root}>
      <Grid container direction='row'>
        <Grid item xs={12} md={12}>
          <Paper className={classes.paper}>
            <Accordion children=''
              id={label}
              expandedLayer={expandedLayer}
              onToggleExpand={onToggleExpand}
              header={<LayerSettingsHeader label={label}
                isSelected={defaultLayer}
                onToggle={onToggleLayer}/>}
              details={Details}
              detailsRootOverride={classes.accordionDetails} />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default GeofenceLayerSettings;
